import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Scissors,
  UserCog,
  Settings,
  LogOut,
  Menu,
  X,
  ClipboardList,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import TrialBanner from "@/components/TrialBanner";

const allNavItems = [
  {
    path: "/dashboard",
    icon: LayoutDashboard,
    label: "Dashboard",
    allowedRoles: ["owner", "staff"],
  },
  {
    path: "/dashboard/calendar",
    icon: Calendar,
    label: "Calendar",
    allowedRoles: ["owner", "staff"],
  },
  {
    path: "/dashboard/appointments",
    icon: ClipboardList,
    label: "Appointments",
    allowedRoles: ["owner"],
  },
  {
    path: "/dashboard/services",
    icon: Scissors,
    label: "Services",
    allowedRoles: ["owner"],
  },
  {
    path: "/dashboard/staff",
    icon: UserCog,
    label: "Staff",
    allowedRoles: ["owner"],
  },
  {
    path: "/dashboard/customers",
    icon: Users,
    label: "Customers",
    allowedRoles: ["owner"],
  },
  {
    path: "/dashboard/payments",
    icon: CreditCard,
    label: "Payments",
    allowedRoles: ["owner"],
  },
  {
    path: "/dashboard/settings",
    icon: Settings,
    label: "Settings",
    allowedRoles: ["owner"],
  },
];

export const DashboardLayout = ({ children }) => {
  const { user, business, logout } = useAuth();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth < 1280) {
        return true;
      }
      return localStorage.getItem("sidebar-collapsed") === "true";
    }
    return false;
  });

  // Filter nav items based on user role
  const navItems = allNavItems.filter((item) =>
    item.allowedRoles.includes(user?.role),
  );

  // ✅ Auto collapse on iPad / smaller screens
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280) {
        setCollapsed(true);
      } else {
        const saved = localStorage.getItem("sidebar-collapsed");
        setCollapsed(saved === "true");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Persist state
  React.useEffect(() => {
    localStorage.setItem("sidebar-collapsed", collapsed);
  }, [collapsed]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm sticky top-0 z-50 flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition-all"
        >
          <Menu className="w-6 h-6 text-slate-700" />
        </button>

        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center shadow-lg">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-xl">
            <span className="text-blue-600">Slot</span>
            <span className="text-slate-800">Book</span>
          </span>
        </div>

        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
          <span className="text-white text-sm font-bold">
            {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full bg-white border-r border-slate-200 shadow-sm z-50 transition-all duration-300 ease-in-out",
          collapsed ? "w-[88px]" : "w-[260px]",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div
            className={cn(
              "flex items-center px-4 py-3 border-b border-slate-200 relative",
              collapsed ? "justify-center" : "justify-between",
            )}
          >
            {/* Expand button - shows on right edge when collapsed */}
            {collapsed && (
              <button
                onClick={() => setCollapsed(false)}
                className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border border-slate-200 rounded-full shadow-sm hidden lg:flex items-center justify-center hover:bg-slate-50 transition z-10"
                title="Expand sidebar"
              >
                <svg
                  className="w-3 h-3 text-slate-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center shadow-md">
                <Calendar className="w-5 h-5 text-white" />
              </div>

              {!collapsed && (
                <span className="font-heading font-bold text-xl">
                  <span className="text-blue-600">Slot</span>
                  <span className="text-slate-800">Book</span>
                </span>
              )}
            </Link>

            {!collapsed && (
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="hidden lg:flex p-2 rounded-lg hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 flex flex-col items-center pt-4 space-y-1.5">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <div
                  key={item.path}
                  className="relative group w-full flex justify-center"
                >
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center justify-center rounded-lg transition-all duration-200",
                      collapsed
                        ? "h-9 w-9"
                        : "gap-3 px-4 py-3 w-full justify-start",
                      isActive
                        ? "bg-[#2B4C7E] text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                    )}
                  >
                    <item.icon className="w-[22px] h-[22px]" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>

                  {/* Tooltip */}
                  {collapsed && (
                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition pointer-events-none">
                      {item.label}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          {!collapsed && (
            <>
              {business && (
                <div className="px-6 py-3 border-t border-slate-200">
                  <p className="text-xs text-slate-400 uppercase">Business</p>
                  <p className="font-semibold text-slate-900 truncate">
                    {business.name}
                  </p>
                </div>
              )}

              <div className="p-4 border-t border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                    <span className="text-white font-bold">
                      {user?.name?.[0]?.toUpperCase() ||
                        user?.email?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {user?.name || "User"}
                    </p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  onClick={logout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </>
          )}
          {collapsed && (
            <div className="p-4 border-t border-slate-200 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="w-10 h-10 p-0 text-slate-600 hover:bg-slate-50 hover:text-red-600"
                onClick={logout}
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main
        className={cn(
          "min-h-screen transition-all duration-300",
          collapsed ? "lg:ml-[88px]" : "lg:ml-[260px]",
        )}
      >
        <TrialBanner />
        <div className="p-5 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;
